import { useState, useRef } from "react";

import { motion } from "framer-motion";

const OctagonBadge = ({
  badge,
  isEarned = false,
  onClick,
  index = 0,
  size = "md",
  showTooltip = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const badgeRef = useRef(null);

  // Size configurations
  const sizes = {
    xs: { container: 50, icon: 10, border: 2, innerBorder: 1, stars: 4 },
    sm: { container: 80, icon: 16, border: 3, innerBorder: 2, stars: 6 },
    md: { container: 100, icon: 22, border: 4, innerBorder: 3, stars: 8 },
    lg: { container: 130, icon: 28, border: 5, innerBorder: 4, stars: 10 },
  };

  const currentSize = sizes[size] || sizes.md;

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Get gradient colors
  const gradientColors = badge.gradient || ["#6B7280", "#4B5563", "#9CA3AF"];

  // Get rarity color
  const getRarityColor = (color) => {
    const colors = {
      gray: "#9CA3AF",
      green: "#22C55E",
      blue: "#3B82F6",
      purple: "#A855F7",
      orange: "#F97316",
      pink: "#EC4899",
    };

    return colors[color] || colors.gray;
  };

  const rarityColor = badge.rarity
    ? getRarityColor(badge.rarity.color)
    : "#9CA3AF";

  // Determine badge tier for decorations and shape
  const getBadgeTier = () => {
    // Sunburst type overrides to tier 7
    if (badge.badgeType === "sunburst") return 7;
    if (!badge.rarity) return 1;
    const tierMap = {
      gray: 1,
      green: 2,
      blue: 3,
      purple: 4,
      orange: 5,
      pink: 6,
      gold: 7, // Sunburst tier
    };

    return tierMap[badge.rarity.color] || 1;
  };

  const badgeTier = getBadgeTier();
  const isSunburst = badgeTier === 7;

  // Center gradient colors for sunburst badges
  const centerColors = badge.centerGradient || [
    "#4CAF50",
    "#2E7D32",
    "#81C784",
  ];

  // Shape clip paths based on tier
  const getShapeClipPath = () => {
    // Tier 7: Sunburst uses circle for the center
    if (isSunburst) {
      return "circle(50% at 50% 50%)";
    }
    // Tier 1-2: Circle
    if (badgeTier <= 2) {
      return "circle(50% at 50% 50%)";
    }
    // Tier 3: Hexagon
    if (badgeTier === 3) {
      return "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";
    }
    // Tier 4: Octagon
    if (badgeTier === 4) {
      return "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)";
    }

    // Tier 5-6: Shield
    return "polygon(50% 0%, 100% 0%, 100% 65%, 50% 100%, 0% 65%, 0% 0%)";
  };

  // Get shape name for display
  const getShapeName = () => {
    if (isSunburst) return "sunburst";
    if (badgeTier <= 2) return "circle";
    if (badgeTier === 3) return "hexagon";
    if (badgeTier === 4) return "octagon";

    return "shield";
  };

  // Generate sunburst rays SVG path
  const generateSunburstRays = (rayCount = 12) => {
    const rays = [];
    const innerRadius = 32;
    const outerRadius = 48;
    const angleStep = (Math.PI * 2) / rayCount;
    const rayWidth = angleStep * 0.5;

    for (let i = 0; i < rayCount; i++) {
      const angle = angleStep * i - Math.PI / 2;
      const x1 = 50 + innerRadius * Math.cos(angle - rayWidth / 2);
      const y1 = 50 + innerRadius * Math.sin(angle - rayWidth / 2);
      const x2 = 50 + outerRadius * Math.cos(angle);
      const y2 = 50 + outerRadius * Math.sin(angle);
      const x3 = 50 + innerRadius * Math.cos(angle + rayWidth / 2);
      const y3 = 50 + innerRadius * Math.sin(angle + rayWidth / 2);

      rays.push({ x1, y1, x2, y2, x3, y3 });
    }

    return rays;
  };

  // Generate laurel leaf positions
  const generateLaurelLeaves = (side) => {
    const leaves = [];
    const leafCount = 5;

    for (let i = 0; i < leafCount; i++) {
      const t = i / (leafCount - 1);
      const baseX = side === "left" ? 20 : 80;
      const x = baseX + (side === "left" ? 1 : -1) * (i * 3);
      const y = 75 - (1 - t * 0.6) * 20;
      const angle = side === "left" ? -30 + i * 10 : 30 - i * 10;
      const scale = 1 - i * 0.1;

      leaves.push({ x, y, angle, scale });
    }

    return leaves;
  };

  const shapeClipPath = getShapeClipPath();
  const shapeName = getShapeName();

  // Get stud positions based on shape
  const getStudPositions = () => {
    // Sunburst doesn't use studs - it uses rays instead
    if (shapeName === "sunburst") {
      return [];
    }
    if (shapeName === "circle") {
      // 8 positions around circle
      return [0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
        const rad = ((angle - 90) * Math.PI) / 180;
        const radius = 46;

        return {
          x: 50 + radius * Math.cos(rad),
          y: 50 + radius * Math.sin(rad),
        };
      });
    }
    if (shapeName === "hexagon") {
      // 6 positions at hexagon vertices
      return [0, 60, 120, 180, 240, 300].map((angle) => {
        const rad = ((angle - 90) * Math.PI) / 180;
        const radius = 46;

        return {
          x: 50 + radius * Math.cos(rad),
          y: 50 + radius * Math.sin(rad),
        };
      });
    }
    if (shapeName === "octagon") {
      // 8 positions at octagon vertices
      return [0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
        const rad = ((angle - 90) * Math.PI) / 180;
        const radius = 46;

        return {
          x: 50 + radius * Math.cos(rad),
          y: 50 + radius * Math.sin(rad),
        };
      });
    }

    // Shield - positions along the edges
    return [
      { x: 50, y: 4 },
      { x: 96, y: 4 },
      { x: 96, y: 60 },
      { x: 50, y: 96 },
      { x: 4, y: 60 },
      { x: 4, y: 4 },
    ];
  };

  // Animation variants for scale-in effect
  const badgeVariants = {
    hidden: {
      scale: 0,
      opacity: 0,
      rotateY: -180,
    },
    visible: {
      scale: 1,
      opacity: 1,
      rotateY: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: index * 0.08,
      },
    },
  };

  // Hover animation - tilt and scale, then return to normal
  const hoverAnimation = {
    scale: [1, 1.12, 1.08],
    rotateY: [0, -12, 8, 0],
    rotateX: [0, 8, -5, 0],
  };

  // Generate star positions around the badge
  const getStarPositions = (count) => {
    const positions = [];
    const startAngle = -90; // Start from top

    for (let i = 0; i < count; i++) {
      const angle = startAngle + (360 / count) * i;
      const rad = (angle * Math.PI) / 180;
      const radius = 38; // percentage from center

      positions.push({
        x: 50 + radius * Math.cos(rad),
        y: 50 + radius * Math.sin(rad),
        angle,
      });
    }

    return positions;
  };

  // Get top highlight position based on shape
  const getTopHighlight = () => {
    if (shapeName === "sunburst") {
      return { left: "30%", right: "30%", top: 4 };
    }
    if (shapeName === "circle") {
      return { left: "25%", right: "25%", top: 2 };
    }
    if (shapeName === "hexagon") {
      return { left: "30%", right: "30%", top: 4 };
    }
    if (shapeName === "shield") {
      return { left: "15%", right: "15%", top: 2 };
    }

    // Octagon
    return { left: "32%", right: "32%", top: 1 };
  };

  const topHighlight = getTopHighlight();

  return (
    <motion.div
      className="relative group cursor-pointer"
      initial="hidden"
      ref={badgeRef}
      style={{
        width: currentSize.container,
        height: currentSize.container + 20, // Extra space for ribbon
        perspective: 800,
      }}
      variants={badgeVariants}
      viewport={{ once: true, amount: 0.3 }}
      whileInView="visible"
      onClick={() => onClick && onClick(badge)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      {/* 3D Badge Container */}
      <motion.div
        animate={
          isHovered ? hoverAnimation : { scale: 1, rotateX: 0, rotateY: 0 }
        }
        className="relative"
        style={{
          width: currentSize.container,
          height: currentSize.container,
          transformStyle: "preserve-3d",
        }}
        transition={{
          duration: 0.6,
          ease: "easeOut",
          times: [0, 0.3, 0.6, 1],
        }}
      >
        {/* Outer glow effect */}
        {isEarned && (
          <motion.div
            animate={{
              opacity: isHovered ? 0.9 : 0.5,
              scale: isHovered ? 1.2 : 1.1,
            }}
            className="absolute inset-0"
            style={{
              clipPath: shapeClipPath,
              background: `radial-gradient(circle at center, ${rarityColor}60 0%, ${rarityColor}20 50%, transparent 70%)`,
              filter: "blur(15px)",
              transform: "translateZ(-20px)",
            }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Shadow layer */}
        <div
          className="absolute inset-0"
          style={{
            clipPath:
              shapeName === "circle" || shapeName === "sunburst"
                ? "none"
                : shapeClipPath,
            borderRadius:
              shapeName === "circle" || shapeName === "sunburst" ? "50%" : 0,
            background: "rgba(0,0,0,0.6)",
            transform: "translateZ(-10px) translateY(5px)",
            filter: "blur(10px)",
          }}
        />

        {/* Sunburst SVG - rays, laurel, and ribbon */}
        {isSunburst && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              filter: isEarned ? "none" : "saturate(0) brightness(0.6)",
              zIndex: 0,
            }}
            viewBox="0 0 100 100"
          >
            <defs>
              <linearGradient
                id={`rayGrad-${badge.id}`}
                x1="0%"
                x2="100%"
                y1="0%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={isEarned ? gradientColors[2] : "#6B7280"}
                />
                <stop
                  offset="100%"
                  stopColor={isEarned ? gradientColors[1] : "#374151"}
                />
              </linearGradient>
              <linearGradient
                id={`laurelGrad-${badge.id}`}
                x1="0%"
                x2="100%"
                y1="0%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={isEarned ? gradientColors[2] : "#6B7280"}
                />
                <stop
                  offset="50%"
                  stopColor={isEarned ? gradientColors[0] : "#4B5563"}
                />
                <stop
                  offset="100%"
                  stopColor={isEarned ? gradientColors[1] : "#374151"}
                />
              </linearGradient>
              <filter
                height="140%"
                id="rayShadow"
                width="140%"
                x="-20%"
                y="-20%"
              >
                <feDropShadow
                  dx="0"
                  dy="1"
                  floodOpacity="0.3"
                  stdDeviation="1"
                />
              </filter>
            </defs>

            {/* Sunburst rays */}
            <g filter="url(#rayShadow)">
              {generateSunburstRays(12).map((ray, i) => (
                <polygon
                  fill={`url(#rayGrad-${badge.id})`}
                  key={i}
                  points={`50,50 ${ray.x1},${ray.y1} ${ray.x2},${ray.y2} ${ray.x3},${ray.y3}`}
                />
              ))}
            </g>

            {/* Left laurel wreath */}
            <g>
              {generateLaurelLeaves("left").map((leaf, i) => (
                <ellipse
                  cx={leaf.x}
                  cy={leaf.y}
                  fill={`url(#laurelGrad-${badge.id})`}
                  key={`left-${i}`}
                  rx={4 * leaf.scale}
                  ry={2 * leaf.scale}
                  transform={`rotate(${leaf.angle} ${leaf.x} ${leaf.y})`}
                />
              ))}
            </g>

            {/* Right laurel wreath */}
            <g>
              {generateLaurelLeaves("right").map((leaf, i) => (
                <ellipse
                  cx={leaf.x}
                  cy={leaf.y}
                  fill={`url(#laurelGrad-${badge.id})`}
                  key={`right-${i}`}
                  rx={4 * leaf.scale}
                  ry={2 * leaf.scale}
                  transform={`rotate(${leaf.angle} ${leaf.x} ${leaf.y})`}
                />
              ))}
            </g>
          </svg>
        )}

        {/* Crown decoration for legendary+ badges */}
        {isEarned && badgeTier >= 5 && (
          <motion.div
            animate={{ y: isHovered ? -2 : 0 }}
            className="absolute left-1/2 pointer-events-none"
            style={{
              top: -12,
              transform: "translateX(-50%) translateZ(15px)",
            }}
            transition={{ duration: 0.3 }}
          >
            <i
              className="fas fa-crown"
              style={{
                fontSize: 16,
                color: gradientColors[2],
                filter: `drop-shadow(0 2px 4px ${gradientColors[1]}80)`,
              }}
            />
          </motion.div>
        )}

        {/* Main badge outer ring - metallic effect */}
        <div
          className="absolute inset-0"
          style={{
            clipPath: shapeClipPath,
            background: isEarned
              ? `
                conic-gradient(
                  from 0deg,
                  ${gradientColors[0]} 0deg,
                  ${gradientColors[2]} 45deg,
                  ${gradientColors[0]} 90deg,
                  ${gradientColors[1]} 135deg,
                  ${gradientColors[0]} 180deg,
                  ${gradientColors[2]} 225deg,
                  ${gradientColors[0]} 270deg,
                  ${gradientColors[1]} 315deg,
                  ${gradientColors[0]} 360deg
                )
              `
              : `
                conic-gradient(
                  from 0deg,
                  #4B5563 0deg,
                  #6B7280 60deg,
                  #374151 120deg,
                  #4B5563 180deg,
                  #374151 240deg,
                  #6B7280 300deg,
                  #4B5563 360deg
                )
              `,
            transform: "translateZ(0px)",
          }}
        >
          {/* Decorative dots/studs around the border */}
          {isEarned &&
            badgeTier >= 2 &&
            getStudPositions().map((pos, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: `radial-gradient(circle at 30% 30%, ${gradientColors[2]}, ${gradientColors[0]})`,
                  transform: "translate(-50%, -50%)",
                  boxShadow: `0 1px 2px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.4)`,
                }}
              />
            ))}

          {/* Inner metallic ring */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              clipPath: shapeClipPath,
              inset: currentSize.border + 2,
              background: isEarned
                ? `
                  linear-gradient(
                    135deg,
                    ${gradientColors[2]}aa 0%,
                    ${gradientColors[0]}dd 25%,
                    ${gradientColors[1]}aa 50%,
                    ${gradientColors[0]}dd 75%,
                    ${gradientColors[2]}aa 100%
                  )
                `
                : `
                  linear-gradient(
                    135deg,
                    #374151 0%,
                    #1F2937 50%,
                    #374151 100%
                  )
                `,
            }}
          >
            {/* Inner dark area with depth */}
            <div
              className="absolute flex items-center justify-center"
              style={{
                clipPath: shapeClipPath,
                inset: currentSize.innerBorder,
                background: isEarned
                  ? isSunburst
                    ? `
                    radial-gradient(
                      ellipse at 35% 35%,
                      ${centerColors[2]} 0%,
                      ${centerColors[0]} 40%,
                      ${centerColors[1]} 100%
                    )
                  `
                    : `
                    radial-gradient(
                      ellipse at 35% 35%,
                      ${gradientColors[2]}50 0%,
                      ${gradientColors[1]}30 30%,
                      ${gradientColors[0]}50 60%,
                      ${gradientColors[1]}70 100%
                    )
                  `
                  : `
                    radial-gradient(
                      ellipse at 30% 30%,
                      #374151 0%,
                      #1F2937 50%,
                      #111827 100%
                    )
                  `,
                boxShadow: isEarned
                  ? isSunburst
                    ? `inset 0 4px 20px ${centerColors[2]}80, inset 0 -4px 20px rgba(0,0,0,0.5)`
                    : `inset 0 3px 15px ${gradientColors[1]}60, inset 0 -3px 15px rgba(0,0,0,0.6)`
                  : "inset 0 2px 10px rgba(0,0,0,0.3), inset 0 -2px 10px rgba(0,0,0,0.5)",
              }}
            >
              {/* Decorative inner border */}
              <div
                className="absolute"
                style={{
                  clipPath: shapeClipPath,
                  inset: 4,
                  border: isEarned
                    ? isSunburst
                      ? `2px solid ${centerColors[2]}40`
                      : `2px solid ${gradientColors[2]}50`
                    : "1px solid rgba(255,255,255,0.05)",
                }}
              />

              {/* Small stars decoration for tier 4+ */}
              {isEarned &&
                badgeTier >= 4 &&
                getStarPositions(6).map((pos, i) => (
                  <motion.i
                    animate={
                      isHovered
                        ? {
                            opacity: [0.6, 1, 0.6],
                            scale: [1, 1.2, 1],
                          }
                        : {}
                    }
                    className="fas fa-star absolute pointer-events-none"
                    key={i}
                    style={{
                      left: `${pos.x}%`,
                      top: `${pos.y}%`,
                      fontSize: 5,
                      color: gradientColors[2],
                      transform: "translate(-50%, -50%)",
                      opacity: 0.7,
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}

              {/* Center emblem area */}
              <div
                className="absolute flex items-center justify-center"
                style={{
                  inset: "20%",
                  background: isEarned
                    ? `radial-gradient(circle at 40% 40%, ${gradientColors[2]}40 0%, transparent 60%)`
                    : "transparent",
                  borderRadius: "50%",
                  overflow: "visible",
                }}
              >
                {/* Icon container with inner circle effect */}
                <div
                  className="relative flex items-center justify-center"
                  style={{
                    width: "80%",
                    height: "80%",
                    borderRadius: "50%",
                    background: isEarned
                      ? `radial-gradient(circle at 30% 30%, ${gradientColors[2]}20 0%, transparent 70%)`
                      : "transparent",
                    overflow: "visible",
                  }}
                >
                  {/* Icon */}
                  <motion.div
                    animate={{
                      scale: isHovered && isEarned ? 1.15 : 1,
                      rotate: isHovered && isEarned ? [0, -5, 5, 0] : 0,
                    }}
                    className="relative"
                    style={{ transform: "translateZ(10px)" }}
                    transition={{ duration: 0.4 }}
                  >
                    {/* Icon glow */}
                    {isEarned && (
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{
                          filter: "blur(10px)",
                          opacity: isHovered ? 0.9 : 0.5,
                        }}
                      >
                        <i
                          className={`fas ${badge.icon}`}
                          style={{
                            fontSize: currentSize.icon * 1.25,
                            color: gradientColors[2],
                          }}
                        />
                      </div>
                    )}
                    {/* Main icon */}
                    <i
                      className={`fas ${badge.icon}`}
                      style={{
                        fontSize: currentSize.icon * 1.25,
                        color: isEarned ? "#FFFFFF" : "#4B5563",
                        textShadow: isEarned
                          ? `0 2px 4px rgba(0,0,0,0.5), 0 0 25px ${gradientColors[2]}80`
                          : "none",
                        filter: isEarned
                          ? "drop-shadow(0 2px 3px rgba(0,0,0,0.4))"
                          : "none",
                      }}
                    />
                  </motion.div>
                </div>
              </div>

              {/* Lock overlay for locked badges */}
              {!isEarned && (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)",
                    clipPath: shapeClipPath,
                  }}
                >
                  <div
                    className="absolute bottom-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #4B5563 0%, #1F2937 100%)",
                      border: "2px solid #6B7280",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    }}
                  >
                    <i
                      className="fas fa-lock"
                      style={{ fontSize: 9, color: "#9CA3AF" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Glare/shine effect */}
        {isEarned && isHovered && (
          <div
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{
              clipPath: shapeClipPath,
              transform: "translateZ(5px)",
            }}
          >
            <motion.div
              animate={{ x: ["-100%", "200%"] }}
              className="absolute inset-0"
              initial={{ x: "-100%" }}
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
              }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            />
          </div>
        )}

        {/* Top edge highlight */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: topHighlight.top,
            left: topHighlight.left,
            right: topHighlight.right,
            height: 2,
            background: isEarned
              ? `linear-gradient(90deg, transparent, ${gradientColors[2]}, transparent)`
              : "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
            transform: "translateZ(3px)",
            borderRadius: 1,
          }}
        />
      </motion.div>

      {/* Ribbon at bottom */}
      {isEarned && (
        <div
          className="absolute left-1/2 pointer-events-none"
          style={{
            bottom: 0,
            transform: "translateX(-50%)",
            width: currentSize.container * 0.7,
            height: 18,
          }}
        >
          {/* Ribbon background */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
              clipPath: "polygon(0% 0%, 100% 0%, 100% 70%, 50% 100%, 0% 70%)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          />
          {/* Ribbon shine */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "40%",
              background: `linear-gradient(180deg, ${gradientColors[2]}40 0%, transparent 100%)`,
              clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
            }}
          />
          {/* Ribbon fold shadows */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: 6,
              height: "70%",
              background: `linear-gradient(90deg, rgba(0,0,0,0.2) 0%, transparent 100%)`,
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              width: 6,
              height: "70%",
              background: `linear-gradient(-90deg, rgba(0,0,0,0.2) 0%, transparent 100%)`,
            }}
          />
          {/* Star on ribbon for high tier */}
          {badgeTier >= 4 && (
            <i
              className="fas fa-star absolute left-1/2 top-1/2"
              style={{
                transform: "translate(-50%, -60%)",
                fontSize: 8,
                color: gradientColors[2],
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
              }}
            />
          )}
        </div>
      )}

      {/* Rarity stars below ribbon */}
      {isEarned && badge.rarity && (
        <div
          className="absolute left-1/2 flex gap-0.5 pointer-events-none"
          style={{
            bottom: -4,
            transform: "translateX(-50%)",
          }}
        >
          {[...Array(Math.min(badgeTier, 5))].map((_, i) => (
            <motion.i
              animate={isHovered ? { scale: [1, 1.2, 1] } : {}}
              className="fas fa-star"
              key={i}
              style={{
                fontSize: 6,
                color: rarityColor,
                filter: `drop-shadow(0 0 2px ${rarityColor})`,
              }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
            />
          ))}
        </div>
      )}

      {/* Tooltip - appears below the badge */}
      {showTooltip && (
        <motion.div
          animate={{
            opacity: isHovered ? 1 : 0,
            y: isHovered ? 0 : -8,
            scale: isHovered ? 1 : 0.9,
            pointerEvents: isHovered ? "auto" : "none",
          }}
          className="absolute left-1/2 whitespace-nowrap"
          initial={{ opacity: 0, y: -8, scale: 0.9 }}
          style={{
            top: "calc(100% + 16px)",
            transform: "translateX(-50%)",
            zIndex: 9999,
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div
            className="relative px-4 py-2.5 rounded-xl text-white text-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)",
              border: "1px solid rgba(75,85,99,0.5)",
              boxShadow:
                "0 10px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Tooltip arrow - now on top */}
            <div
              className="absolute left-1/2 -top-2"
              style={{
                transform: "translateX(-50%) rotate(45deg)",
                width: 10,
                height: 10,
                background:
                  "linear-gradient(135deg, rgba(17,24,39,0.95) 50%, transparent 50%)",
                borderLeft: "1px solid rgba(75,85,99,0.5)",
                borderTop: "1px solid rgba(75,85,99,0.5)",
              }}
            />
            <div className="font-bold text-sm mb-0.5">{badge.name}</div>
            <div className="text-gray-400 text-xs">{badge.description}</div>
            {!isEarned && (
              <div className="flex items-center justify-center gap-1.5 mt-1.5 text-yellow-500 text-xs">
                <i className="fas fa-lock text-[9px]" />
                <span>Locked</span>
              </div>
            )}
            {isEarned && badge.rarity && (
              <div
                className="text-xs mt-1.5 font-semibold"
                style={{ color: rarityColor }}
              >
                {badge.rarity.name}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OctagonBadge;
